import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import apiEndPointsConfig from "../../../services/apiEndPoints";

import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  Grid,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Fade,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  InputLabel,
  Badge,
  Card,
  CardContent,
  Tabs,
  Tab,
} from "@mui/material";

import {
  Search,
  RefreshCw,
  Eye,
  Download,
  Users as UsersIcon,
  UserCheck,
  UserX,
  ChevronUp,
  ChevronDown,
  Mail,
  Phone,
  Building2,
  X,
  MapPin,
  Calendar,
  Shield,
  Users,
  PhoneCall,
  Globe,
  FileText,
  Ticket,
  TrendingUp,
  Filter,
} from "lucide-react";

import { alpha, useTheme } from "@mui/material/styles";
import axiosInstance from "../../../services/axiosInstance";
import { exportLeadsToExcel } from "../../../components/exportLeadsToExcel";

/* -----------------------------------------
   Enhanced Detail Row Component
------------------------------------------ */
const DetailRow = ({ icon: Icon, label, value, color = "#3b82f6" }) => (
  <Stack direction="row" spacing={2} alignItems="flex-start" py={1.5}>
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: alpha(color, 0.1),
        display: "inline-flex",
        flexShrink: 0,
        border: `1px solid ${alpha(color, 0.2)}`,
      }}
    >
      <Icon size={18} color={color} strokeWidth={2} />
    </Box>
    <Box flex={1}>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        fontWeight={500}
        textTransform="uppercase"
        letterSpacing={0.5}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} mt={0.5}>
        {value || "—"}
      </Typography>
    </Box>
  </Stack>
);

/* -----------------------------------------
   Enhanced User Detail Modal
------------------------------------------ */
const UserDetailModal = ({ open, onClose, user }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  if (!user) return null;

  const formatDate = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  const getStatusColor = (status) => {
    const map = {
      active: "#22c55e",
      demo: "#f59e0b",
      paid: "#3b82f6",
      cancelled: "#ef4444",
    };
    return map[status?.toLowerCase()] || "#94a3b8";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[24],
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: "primary.main",
                fontSize: 16,
                fontWeight: 700,
                boxShadow: theme.shadows[3],
              }}
            >
              {user.name?.charAt(0).toUpperCase() || "U"}
            </Avatar>

            <Box>
              <Typography variant="h5" fontWeight={700}>
                {user.name}
              </Typography>
              <Stack direction="row" spacing={1} mt={1}>
                <Chip
                  label={user.subscriptionStatus || "Unknown"}
                  size="small"
                  sx={{
                    bgcolor: alpha(
                      getStatusColor(user.subscriptionStatus),
                      0.15
                    ),
                    color: getStatusColor(user.subscriptionStatus),
                    fontWeight: 600,
                    borderRadius: 1.5,
                    border: `1px solid ${alpha(
                      getStatusColor(user.subscriptionStatus),
                      0.3
                    )}`,
                  }}
                />
                <Chip
                  label={user.loginMethod}
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: 1.5 }}
                />
              </Stack>
            </Box>
          </Stack>

          <IconButton
            onClick={onClose}
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.error.main, 0.2),
              },
            }}
          >
            <X size={20} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 2, px: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Contact & Company" />
          <Tab label="System Info" />
        </Tabs>

        {activeTab === 0 && (
          <Grid container spacing={3}>
            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                    <Mail size={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" fontWeight={700}>
                      Contact Information
                    </Typography>
                  </Stack>

                  <DetailRow icon={Mail} label="Email" value={user.email} />
                  <DetailRow
                    icon={Phone}
                    label="Phone"
                    value={user.phoneNumber}
                    color="#10b981"
                  />
                  <DetailRow
                    icon={MapPin}
                    label="Country"
                    value={user.country}
                    color="#f59e0b"
                  />
                  <DetailRow
                    icon={PhoneCall}
                    label="Customer Care"
                    value={user.customerCareNo}
                    color="#8b5cf6"
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Company Info */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                    <Building2 size={20} color={theme.palette.primary.main} />
                    <Typography variant="h6" fontWeight={700}>
                      Company Info
                    </Typography>
                  </Stack>

                  <DetailRow
                    icon={Building2}
                    label="Company"
                    value={user.companyName}
                    color="#0ea5e9"
                  />
                  <DetailRow
                    icon={Users}
                    label="Industry"
                    value={user.industryType}
                    color="#ec4899"
                  />
                  <DetailRow
                    icon={PhoneCall}
                    label="Call Type"
                    value={user.callType}
                    color="#f97316"
                  />
                  <DetailRow
                    icon={Globe}
                    label="Google Sheets"
                    value={user.googleSheetsUrl ? "Connected" : "Not Connected"}
                    color={user.googleSheetsUrl ? "#22c55e" : "#94a3b8"}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <Shield size={20} color={theme.palette.primary.main} />
                <Typography variant="h6" fontWeight={700}>
                  System Metadata
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DetailRow
                    icon={Calendar}
                    label="Created At"
                    value={formatDate(user.createdAt)}
                    color="#3b82f6"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailRow
                    icon={Calendar}
                    label="Updated At"
                    value={formatDate(user.updatedAt)}
                    color="#8b5cf6"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailRow
                    icon={Shield}
                    label="Login Method"
                    value={user.loginMethod}
                    color="#10b981"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DetailRow
                    icon={TrendingUp}
                    label="Dropoff Status"
                    value={user.dropoff || "N/A"}
                    color="#ef4444"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2.5 }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            borderRadius: 2,
            px: 4,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* -----------------------------------------
   Enhanced Stats Card Component
------------------------------------------ */

/* -----------------------------------------
   Main Users Table Component
------------------------------------------ */
const UsersTable = () => {
  const theme = useTheme();
  const token = localStorage.getItem("token");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });

  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* Load Data */
  useEffect(() => {
    getAllUsers();
  }, []);

  const getAllUsers = async () => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(apiEndPointsConfig.listOfLeads, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const filtered = (res.data?.data || []).filter(
        (x) => x.dropoff !== "demo"
      );

      setData(filtered);
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to load users"
      );
    } finally {
      setLoading(false);
    }
  };

  /* Filters */
  const countries = [...new Set(data.map((d) => d.country))].filter(Boolean);
  const statuses = [...new Set(data.map((d) => d.subscriptionStatus))].filter(
    Boolean
  );

  const filteredData = useMemo(() => {
    let users = [...data];

    if (searchTerm) {
      users = users.filter(
        (u) =>
          u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (countryFilter !== "all")
      users = users.filter((u) => u.country === countryFilter);
    if (statusFilter !== "all")
      users = users.filter((u) => u.subscriptionStatus === statusFilter);

    return users;
  }, [data, searchTerm, countryFilter, statusFilter]);

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* Sorting */

  const sortedData = useMemo(() => {
    return [...paginatedData].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key.includes("At")) {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else {
        aVal = aVal?.toString().toLowerCase() || "";
        bVal = bVal?.toString().toLowerCase() || "";
      }

      if (sortConfig.direction === "asc") {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
  }, [paginatedData, sortConfig]);

  if (loading)
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
        }}
      >
        <CircularProgress size={56} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Loading users...
        </Typography>
      </Box>
    );

  if (error)
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert
          severity="error"
          sx={{ borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={getAllUsers}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );

  const activeUsers = data.filter((u) =>
    ["active", "paid"].includes(u.subscriptionStatus)
  ).length;

  return (
    <Fade in timeout={600}>
      <Box sx={{ py: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
        <Container maxWidth="xl">
          {/* Filters */}
          <Card
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              mb: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack spacing={0}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    placeholder="Search by name, email, or company..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={16} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        height: "40px",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={countryFilter}
                      label="Country"
                      onChange={(e) => setCountryFilter(e.target.value)}
                      sx={{ borderRadius: 2, height: "40px" }}
                    >
                      <MenuItem value="all">All Countries</MenuItem>
                      {countries.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Download size={16} />}
                    onClick={() => exportLeadsToExcel(filteredData)}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                    }}
                  >
                    Export Leads
                  </Button>
                </Grid>
              </Grid>
            </Stack>
          </Card>

          {/* Table */}
          <Card elevation={0} sx={{ borderRadius: 3, overflow: "hidden" }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>
                      CONTACT
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>
                      USER
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>
                      COMPANY
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>
                      COUNTRY
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>
                      INDUSTRY
                    </TableCell>

                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>
                      JOINED
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, fontSize: 13 }}
                      align="center"
                    >
                      ACTIONS
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sortedData.map((user, index) => (
                    <TableRow
                      key={user._id}
                      sx={{
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                        },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={600}>
                            {user.email}
                          </Typography>
                          {user.phoneNumber && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {user.phoneNumber}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {user.name || "—"}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {user.companyName || "—"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={user.country || "N/A"}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 1.5 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={user.industryType || "N/A"}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: "info.main",
                            fontWeight: 600,
                            borderRadius: 1.5,
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(user.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            }
                          )}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            onClick={() => {
                              setSelectedUser(user);
                              setModalOpen(true);
                            }}
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              "&:hover": {
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                              },
                            }}
                          >
                            <Eye size={18} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />

            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              }}
            />
          </Card>

          {/* Modal */}
          <UserDetailModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            user={selectedUser}
          />
        </Container>
      </Box>
    </Fade>
  );
};

export default UsersTable;
